import { useMemo, useContext } from "react";

// react-table components
import { useTable, usePagination, useSortBy, useFilters } from "react-table";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Icon from "@mui/material/Icon";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import "../DataTable/index.css";

// Material Dashboard 2 PRO React components
import MDAutocomplete from "components/MDAutocomplete";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";

// Material Dashboard 2 PRO React examples
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import { Button, Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { CommonContext } from "context/CommonCtx";
import { ENTRIES_PER_PAGE_OPTIONS } from "utils/constants";
import { DefaultColumnFilter } from "./filters";
import CsvDownloader from "react-csv-downloader";
import { FileDownload } from "@mui/icons-material";
import { Loader } from "rsuite";

function LocalDataTable({
  table = [],
  isSorted = true,
  noEndBorder = false,
  isLoading = false,
  isRowClickable = false,
  showTotalEntries = true,
  entriesPerPage = 10,
  isClearAllFilter = true,
  isCSVExport = false,
  defaultSorting = [],
  onRefresh = () => {},
}) {
  const entries = entriesPerPage.entries
    ? entriesPerPage.entries.map((el) => el.toString())
    : ENTRIES_PER_PAGE_OPTIONS;

  const columns = useMemo(() => table.columns, [table]);

  const data = useMemo(() => table.rows, [table]);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        sortBy: defaultSorting || [],
      },
      defaultColumn: { Filter: DefaultColumnFilter },
    },
    // useGlobalFilter,
    useFilters,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setAllFilters,
    state: { pageIndex, pageSize, filters: allFilters },
  } = tableInstance;

  const navigate = useNavigate();

  const { CCLoader } = useContext(CommonContext);

  const clickableRow = (row) => {
    if (isRowClickable) {
      // CCLoader();
      // navigate(`/app/roles/${row?.original?.id}`);
    }
  };

  // Set the entries per page value based on the select value
  const setEntriesPerPage = (value) => {
    setPageSize(value);
    gotoPage(0);
  };

  // Render the paginations
  const renderPagination = pageOptions.map((option) => (
    <MDPagination
      item
      key={option}
      onClick={() => gotoPage(Number(option))}
      active={pageIndex === option}
    >
      {option + 1}
    </MDPagination>
  ));

  // Handler for the input to set the pagination index
  const handleInputPagination = ({ target: { value } }) =>
    value > pageOptions.length || value < 0 ? gotoPage(0) : gotoPage(Number(value));

  // Customized page options starting from 1
  const customizedPageOptions = pageOptions.map((option) => option + 1);

  // Setting value for the pagination input
  const handleInputPaginationValue = ({ target: value }) => gotoPage(Number(value.value - 1));

  // Search input state handle
  // const onSearchChange = useAsyncDebounce((value) => {
  //   setGlobalFilter(value || undefined);
  // }, 100);

  // A function that sets the sorted value for the table
  const setSortedValue = (column) => {
    let sortedValue;

    if (column?.sort) {
      if (isSorted && column?.isSorted) {
        sortedValue = column.isSortedDesc ? "desc" : "asce";
      }
      // else {
      // sortedValue = "none";
      // }
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  // Setting the entries starting point
  const entriesStart = pageIndex === 0 ? pageIndex + 1 : pageIndex * pageSize + 1;

  // Setting the entries ending point
  let entriesEnd;

  if (pageIndex === 0) {
    entriesEnd = pageSize;
  } else if (pageIndex === pageOptions.length - 1) {
    entriesEnd = rows.length;
  } else {
    entriesEnd = pageSize * (pageIndex + 1);
  }

  const csvColumns = columns
    .filter((column) => column.accessor !== "actions") // Filter out action columns
    .map((column) => {
      return {
        id: column.accessor,
        displayName: column.Header,
      };
    });

  const getCsvRows = () => {
    const csvData = page.map((row) => {
      const temp = { ...row.values };

      // Capitalize the status field if it exists
      if (temp.status) {
        temp.status = temp.status.charAt(0).toUpperCase() + temp.status.slice(1);
      }

      return temp;
    });

    return Promise.resolve(csvData);
  };

  return (
    <>
      {entriesPerPage ? (
        <MDBox className="cc-filter_record" p={2}>
          <MDBox>
            {entriesPerPage && (
              <MDBox display="flex" alignItems="center">
                <MDAutocomplete
                  disableClearable
                  value={pageSize.toString()}
                  options={entries}
                  onChange={(event, newValue) => {
                    setEntriesPerPage(parseInt(newValue, 10));
                  }}
                  size="small"
                  sx={{ width: "5rem" }}
                  renderInput={(params) => <MDInput {...params} />}
                />
                <MDTypography variant="caption" color="secondary" sx={{ textWrap: "nowrap" }}>
                  &nbsp;&nbsp;entries per page
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
          <MDBox display="flex" alignItems="center">
            <MDBox display="flex" alignItems="center">
              {isCSVExport && (
                <CsvDownloader
                  filename="tenant-invoice"
                  extension=".csv"
                  separator=";"
                  columns={csvColumns}
                  datas={getCsvRows}
                >
                  <Button variant="outlined" startIcon={<FileDownload />}>
                    Export to CSV
                  </Button>
                </CsvDownloader>
              )}

              {onRefresh && (
                <Button
                  onClick={() => onRefresh()}
                  disabled={isLoading}
                  startIcon={<RefreshIcon />}
                >
                  Refresh
                </Button>
              )}

              {isClearAllFilter && (
                <Button
                  // disabled={!allFilters.length}
                  onClick={(e) => {
                    setAllFilters([]);
                  }}
                  startIcon={<CloseIcon />}
                >
                  clear All
                </Button>
              )}
            </MDBox>
          </MDBox>
        </MDBox>
      ) : null}
      <TableContainer sx={{ boxShadow: "none" }}>
        <Table {...getTableProps()} className={`cc_table`}>
          <MDBox component="thead">
            {headerGroups.map((headerGroup, key) => {
              const { key: headerKey, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
              return (
                <TableRow key={headerKey || key} {...headerGroupProps}>
                  {headerGroup.headers.map((column, idx) => {
                    const { key, ...headerProps } = column.getHeaderProps(
                      (isSorted || column.sort) && column.getSortByToggleProps()
                    );
                    return (
                      <DataTableHeadCell
                        className="cc-table-row"
                        key={key || idx}
                        {...headerProps}
                        width={column.width ? column.width : "auto"}
                        align={column.align ? column.align : "left"}
                        sorted={setSortedValue(column)}
                        sx={{ borderBottom: "0 !important" }}
                        inputType={column.filterInputType}
                        inputOptions={column.filterOptions}
                        sortable={column.sortable}
                        column={column}
                      >
                        {column.render("Header")}
                      </DataTableHeadCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </MDBox>

          <TableBody {...getTableBodyProps()}>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 15 }}>
                  <Loader size="lg" />
                </TableCell>
              </TableRow>
            ) : table.rows.length > 0 ? (
              <>
                {page.map((row, key) => {
                  prepareRow(row);
                  const { key: rowKey, ...rowProps } = row.getRowProps();
                  return (
                    <TableRow
                      onClick={() => clickableRow(row)}
                      className={`table_row ${isRowClickable ? "clickable_row" : ""} `}
                      key={rowKey || key}
                      {...rowProps}
                    >
                      {row.cells.map((cell, idx) => {
                        const { key: cellKey, ...cellProps } = cell.getCellProps();
                        return (
                          <DataTableBodyCell
                            key={cellKey || idx}
                            noBorder={noEndBorder && rows.length - 1 === key}
                            align={cell.column.align ? cell.column.align : "left"}
                            {...cellProps}
                          >
                            {cell.render("Cell")}
                          </DataTableBodyCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </>
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 10 }}>
                  <MDTypography variant="h6" color="text">
                    Data not found!
                  </MDTypography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {rows.length > 0 && (
          <MDBox
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            p={!showTotalEntries && pageOptions.length === 1 ? 0 : 3}
          >
            {showTotalEntries && (
              <MDBox mb={{ xs: 3, sm: 0 }}>
                <MDTypography variant="button" color="secondary" fontWeight="regular">
                  Showing {entriesStart} to {entriesEnd < rows.length ? entriesEnd : rows.length} of{" "}
                  {rows.length} entries
                </MDTypography>
              </MDBox>
            )}
            {pageOptions.length > 1 && (
              <MDPagination variant="gradient" color="primary">
                {canPreviousPage && (
                  <MDPagination item onClick={() => previousPage()}>
                    <Icon sx={{ fontWeight: "bold", cursor: "pointer" }}>chevron_left</Icon>
                  </MDPagination>
                )}
                {renderPagination.length > 6 ? (
                  <MDBox width="5rem" mx={1} className="roundPaginationButton">
                    <MDInput
                      inputProps={{ type: "number", min: 1, max: customizedPageOptions.length }}
                      value={customizedPageOptions[pageIndex]}
                      onChange={(handleInputPagination, handleInputPaginationValue)}
                    />
                  </MDBox>
                ) : (
                  renderPagination
                )}
                {canNextPage && (
                  <MDPagination item onClick={() => nextPage()}>
                    <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                  </MDPagination>
                )}
              </MDPagination>
            )}
          </MDBox>
        )}
      </TableContainer>
    </>
  );
}

export default LocalDataTable;
